import { RenderStatus } from '@/shared/constants';
import { isRenderInQueue } from '@/shared/lib/render-status';
import {
  removeProgressAnchor,
  setProgressAnchor,
} from '@/shared/lib/progress';
import { JOB_QUEUE_CONFIG } from '@/shared/config/job-queue.config';
import {
  PROCESSING_JOBS_RETENTION_MS,
  lastProcessingJobs,
  recordTerminalState,
  validateJobData,
} from './internals';
import type { Job, RenderJob, UpscaleJob } from '../job-queue.types';
import type { JobQueueStateCreator, QueueMutationsSlice } from './types';

export const createQueueMutationsSlice: JobQueueStateCreator<
  QueueMutationsSlice
> = (set, get) => ({
  replaceJobQueue: (jobs) => {
    const state = get();

    // ===== VALIDATION =====
    // Protect against invalid data
    if (!validateJobData(jobs)) {
      console.error('[JobQueue] Invalid jobs data received:', jobs);
      // Don't update state with invalid data - keep current state
      return;
    }

    // Limit queue size to prevent memory issues
    let limitedJobs = jobs.slice(0, JOB_QUEUE_CONFIG.MAX_QUEUE_LIMIT);
    if (jobs.length > JOB_QUEUE_CONFIG.MAX_QUEUE_LIMIT) {
      console.warn(
        `[JobQueue] Queue size (${jobs.length}) exceeds limit (${JOB_QUEUE_CONFIG.MAX_QUEUE_LIMIT}). Truncating.`
      );
    }

    // ===== PROTECTION FROM DISAPPEARING JOBS =====
    // If a processing job disappears from snapshot, keep it for 60 seconds
    const now = Date.now();
    const currentJobsMap = new Map(state.jobQueue.map((j) => [j.id, j]));
    const newJobsMap = new Map(limitedJobs.map((j) => [j.id, j]));
    const protectedJobs: Job[] = [];
    const resultJobs: Job[] = [];

    // Process each job from the new snapshot with STALE_SNAPSHOT_PROTECTION
    limitedJobs.forEach((newJob) => {
      const currentJob = currentJobsMap.get(newJob.id);

      // STALE SNAPSHOT PROTECTION: Don't downgrade terminal status to active
      if (
        currentJob &&
        !isRenderInQueue(currentJob.status) && // current is terminal (completed/failed/cancelled)
        isRenderInQueue(newJob.status) // new is active (pending/processing)
      ) {
        console.warn(
          `[STALE_SNAPSHOT] Ignoring snapshot update for job ${newJob.id}: would downgrade ${currentJob.status} to ${newJob.status}`
        );
        resultJobs.push(currentJob);
        lastProcessingJobs.delete(newJob.id);
        return;
      }

      // Update protection cache
      if (isRenderInQueue(newJob.status)) {
        // Job is still processing - update or add to cache
        lastProcessingJobs.set(newJob.id, { job: newJob, timestamp: now });
      } else {
        // Job completed/failed - immediately remove from protection cache, anchors, and record terminal state
        lastProcessingJobs.delete(newJob.id);
        removeProgressAnchor(newJob.id);
        recordTerminalState(newJob.id);
      }

      resultJobs.push(newJob);
    });

    // Check if any processing jobs disappeared
    lastProcessingJobs.forEach((cached, jobId) => {
      if (!newJobsMap.has(jobId)) {
        const timeSinceLastSeen = now - cached.timestamp;

        if (timeSinceLastSeen < PROCESSING_JOBS_RETENTION_MS) {
          // Protect disappeared job temporarily
          protectedJobs.push(cached.job);
        } else {
          // Expired - remove from cache
          lastProcessingJobs.delete(jobId);
        }
      }
    });

    // Clean up expired entries
    lastProcessingJobs.forEach((cached, jobId) => {
      if (now - cached.timestamp > PROCESSING_JOBS_RETENTION_MS) {
        lastProcessingJobs.delete(jobId);
      }
    });

    // Merge result jobs with protected jobs
    limitedJobs = [...resultJobs, ...protectedJobs].slice(
      0,
      JOB_QUEUE_CONFIG.MAX_QUEUE_LIMIT
    );

    // ===== CHANGE DETECTION =====
    // Check if the queue has actually changed by comparing IDs and statuses
    const hasChanged =
      state.jobQueue.length !== limitedJobs.length ||
      !state.jobQueue.every(
        (r, i) =>
          limitedJobs[i] &&
          r.id === limitedJobs[i].id &&
          r.status === limitedJobs[i].status
      );

    // Only update if something actually changed
    if (hasChanged) {
      // ===== UNSEEN COMPLETED TRACKING =====
      // Detect jobs that transitioned from active to completed
      const newUnseen = new Set(state.unseenCompletedJobIds);
      limitedJobs.forEach((newJob) => {
        if (newJob.status === RenderStatus.COMPLETED) {
          const oldJob = currentJobsMap.get(newJob.id);
          if (oldJob && isRenderInQueue(oldJob.status)) {
            // Job was processing, now completed → mark as unseen
            newUnseen.add(newJob.id);
          }
        }
      });

      set({
        jobQueue: limitedJobs,
        lastQueueFetch: new Date(),
        ...(newUnseen.size !== state.unseenCompletedJobIds.size
          ? { unseenCompletedJobIds: newUnseen }
          : {}),
      });
    }
  },

  // ===== L1 RECONCILE (WEB-L1) =====
  // Heal the local job queue from any API response. WS events are an
  // optimistic push; this reconcile is the unconditional truth from the API.
  // Two transitions are patched in-place for existing local jobs:
  //   1. active local → terminal API (lost `renders/completed`/`renders/failed`)
  //   2. pending local → processing API (lost `renders/started`)
  // Returns the set of conceptIds for terminal heals so the caller (the L4
  // watchdog) can invalidate downstream caches (e.g. concept angles).
  // See docs/REALTIME_RECONCILE_PLAN.md → WEB-L1.
  reconcileFromApi: (rendersFromApi) => {
    if (rendersFromApi.length === 0) return new Set();

    const state = get();
    const apiById = new Map(rendersFromApi.map((r) => [r.id, r]));

    let touched = false;
    const newUnseen = new Set(state.unseenCompletedJobIds);
    const healedConceptIds = new Set<string>();

    const updatedJobs = state.jobQueue.map((job) => {
      const apiRender = apiById.get(job.id);
      if (!apiRender) return job;

      // Already-terminal local jobs are the final word — never downgraded.
      if (!isRenderInQueue(job.status)) return job;

      const apiStatus = apiRender.attributes.status;
      const apiIsTerminal = !isRenderInQueue(apiStatus);

      if (apiIsTerminal) {
        touched = true;
        lastProcessingJobs.delete(job.id);
        removeProgressAnchor(job.id);
        recordTerminalState(job.id);
        if (apiStatus === RenderStatus.COMPLETED) {
          newUnseen.add(job.id);
        }
        if (job.conceptId) {
          healedConceptIds.add(job.conceptId);
        }

        if (job.jobType === 'render' || job.jobType === 'upscale') {
          return {
            ...job,
            status: apiStatus,
            completedAt: apiRender.attributes.completed_at ?? job.completedAt,
            imageUrl: apiRender.attributes.file_url ?? job.imageUrl,
          } as RenderJob | UpscaleJob;
        }
        return {
          ...job,
          status: apiStatus,
          completedAt: apiRender.attributes.completed_at ?? job.completedAt,
        };
      }

      if (
        job.status === RenderStatus.PENDING &&
        apiStatus === RenderStatus.PROCESSING
      ) {
        touched = true;
        const apiStartedAt =
          apiRender.attributes.started_at ?? job.startedAt;
        const apiDuration =
          apiRender.attributes.duration_predicted ?? job.durationPredicted;
        if (apiDuration > 0) {
          setProgressAnchor(job.id, {
            anchorUptime: performance.now(),
            anchorProgress: 0,
            durationPredicted: apiDuration,
          });
        }
        return {
          ...job,
          status: RenderStatus.PROCESSING,
          startedAt: apiStartedAt,
          durationPredicted: apiDuration,
        };
      }

      return job;
    });

    if (!touched) return healedConceptIds;

    const unseenChanged =
      newUnseen.size !== state.unseenCompletedJobIds.size;
    set({
      jobQueue: updatedJobs,
      ...(unseenChanged ? { unseenCompletedJobIds: newUnseen } : {}),
    });
    return healedConceptIds;
  },

  // ===== L1 ALBUM RECONCILE =====
  // Album sibling of `reconcileFromApi`. Same two transitions for albums
  // already present in the queue. No conceptIds returned — the caller
  // already has the conceptId in scope (album endpoint is concept-scoped).
  reconcileAlbumsFromApi: (albums, conceptId) => {
    if (albums.length === 0) return;

    const state = get();
    const byId = new Map(albums.map((a) => [a.id, a]));

    let touched = false;
    const newUnseen = new Set(state.unseenCompletedJobIds);

    const updatedJobs = state.jobQueue.map((job) => {
      if (job.jobType !== 'album') return job;
      const apiAlbum = byId.get(job.id);
      if (!apiAlbum) return job;
      if (!isRenderInQueue(job.status)) return job;

      if (apiAlbum.status === 'completed' || apiAlbum.status === 'failed') {
        touched = true;
        lastProcessingJobs.delete(job.id);
        removeProgressAnchor(job.id);
        recordTerminalState(job.id);
        if (apiAlbum.status === 'completed') {
          newUnseen.add(job.id);
        }
        return {
          ...job,
          status:
            apiAlbum.status === 'completed'
              ? RenderStatus.COMPLETED
              : RenderStatus.FAILED,
          completedAt: apiAlbum.generated_at ?? job.completedAt,
          albumPublicUrl: apiAlbum.public_url ?? job.albumPublicUrl,
          albumFilename: apiAlbum.filename ?? job.albumFilename,
        };
      }

      if (
        job.status === RenderStatus.PENDING &&
        apiAlbum.status === 'processing'
      ) {
        touched = true;
        const apiStartedAt = apiAlbum.started_at ?? job.startedAt;
        const apiDuration =
          apiAlbum.duration_predicted ?? job.durationPredicted;
        if (apiDuration && apiDuration > 0) {
          setProgressAnchor(job.id, {
            anchorUptime: performance.now(),
            anchorProgress: 0,
            durationPredicted: apiDuration,
          });
        }
        return {
          ...job,
          status: RenderStatus.PROCESSING,
          startedAt: apiStartedAt,
          durationPredicted: apiDuration ?? job.durationPredicted,
        };
      }

      return job;
    });

    if (!touched) {
      // Suppress unused-var warning when conceptId isn't part of the patch.
      void conceptId;
      return;
    }

    const unseenChanged =
      newUnseen.size !== state.unseenCompletedJobIds.size;
    set({
      jobQueue: updatedJobs,
      ...(unseenChanged ? { unseenCompletedJobIds: newUnseen } : {}),
    });
  },

  // ===== L1 ESTIMATE RECONCILE =====
  // Estimate sibling of `reconcileAlbumsFromApi`, fed by the L4 watchdog's
  // GET /estimates/{id} probe. Raw estimate statuses (generating/ready) are
  // mapped to the unified job vocabulary the queue runs on.
  reconcileEstimateFromApi: (estimate) => {
    const state = get();

    let touched = false;
    const newUnseen = new Set(state.unseenCompletedJobIds);

    const updatedJobs = state.jobQueue.map((job) => {
      if (job.jobType !== 'estimate' || job.id !== estimate.id) return job;
      if (!isRenderInQueue(job.status)) return job;

      if (estimate.status === 'ready' || estimate.status === 'failed') {
        touched = true;
        lastProcessingJobs.delete(job.id);
        removeProgressAnchor(job.id);
        recordTerminalState(job.id);
        if (estimate.status === 'ready') {
          newUnseen.add(job.id);
        }
        return {
          ...job,
          status:
            estimate.status === 'ready'
              ? RenderStatus.COMPLETED
              : RenderStatus.FAILED,
          estimateCurrency: estimate.currency ?? job.estimateCurrency,
          estimateGrandTotal: estimate.grand_total ?? job.estimateGrandTotal,
        };
      }

      if (
        job.status === RenderStatus.PENDING &&
        estimate.status === 'generating'
      ) {
        touched = true;
        return { ...job, status: RenderStatus.PROCESSING };
      }

      return job;
    });

    if (!touched) return;

    const unseenChanged =
      newUnseen.size !== state.unseenCompletedJobIds.size;
    set({
      jobQueue: updatedJobs,
      ...(unseenChanged ? { unseenCompletedJobIds: newUnseen } : {}),
    });
  },

  addUpscaleJob: (job) => {
    set((state) => {
      // Check if job already exists
      if (state.jobQueue.some((j) => j.id === job.id)) {
        return state;
      }

      return {
        jobQueue: [job, ...state.jobQueue].slice(
          0,
          JOB_QUEUE_CONFIG.MAX_QUEUE_LIMIT
        ),
      };
    });
  },

  addPendingRenderJob: (job) => {
    set((state) => {
      // Skip if job with same ID already exists
      if (state.jobQueue.some((j) => j.id === job.id)) {
        return state;
      }

      // Remove old failed/cancelled jobs for same conceptAngleId (superseded cleanup)
      const cleaned = state.jobQueue.filter(
        (j) =>
          !(
            j.conceptAngleId === job.conceptAngleId &&
            j.jobType === 'render' &&
            (j.status === RenderStatus.FAILED ||
              j.status === RenderStatus.CANCELLED)
          )
      );

      // Protect from snapshot replacement until server includes this job
      lastProcessingJobs.set(job.id, { job, timestamp: Date.now() });

      return {
        jobQueue: [job, ...cleaned].slice(0, JOB_QUEUE_CONFIG.MAX_QUEUE_LIMIT),
      };
    });
  },

  addPendingAlbumJob: (job) => {
    set((state) => {
      // Skip if job with same ID already exists
      if (state.jobQueue.some((j) => j.id === job.id)) {
        return state;
      }

      // Protect from snapshot replacement until server includes this job
      lastProcessingJobs.set(job.id, { job, timestamp: Date.now() });

      return {
        jobQueue: [job, ...state.jobQueue].slice(
          0,
          JOB_QUEUE_CONFIG.MAX_QUEUE_LIMIT
        ),
      };
    });
  },

  cleanupSupersededJobs: (conceptAngleId, excludeJobId) => {
    set((state) => {
      const filtered = state.jobQueue.filter(
        (j) =>
          !(
            j.conceptAngleId === conceptAngleId &&
            j.id !== excludeJobId &&
            j.jobType === 'render' &&
            (j.status === RenderStatus.FAILED ||
              j.status === RenderStatus.CANCELLED)
          )
      );

      if (filtered.length === state.jobQueue.length) {
        return state; // No change
      }

      return { jobQueue: filtered };
    });
  },

  updateJobStatus: (jobId, status) =>
    set((state) => {
      // Track unseen completed jobs
      const newUnseen =
        status === RenderStatus.COMPLETED
          ? new Set([...state.unseenCompletedJobIds, jobId])
          : state.unseenCompletedJobIds;

      return {
        jobQueue: state.jobQueue.map((job) =>
          job.id === jobId
            ? {
                ...job,
                status,
                completedAt:
                  status === RenderStatus.FAILED ||
                  status === RenderStatus.COMPLETED
                    ? new Date().toISOString()
                    : job.completedAt,
              }
            : job
        ),
        unseenCompletedJobIds: newUnseen,
      };
    }),

  updateJobStarted: (jobId, startedAt, durationPredicted) => {
    set((state) => ({
      jobQueue: state.jobQueue.map((job) => {
        if (job.id !== jobId) return job;

        // Don't downgrade terminal status
        if (
          job.status === RenderStatus.COMPLETED ||
          job.status === RenderStatus.FAILED ||
          job.status === RenderStatus.CANCELLED
        ) {
          return job;
        }

        return {
          ...job,
          status: RenderStatus.PROCESSING,
          startedAt,
          durationPredicted,
        };
      }),
    }));
  },

  updateUpscaleJobStarted: (jobId, startedAt, durationPredicted) => {
    // Create monotonic progress anchor for upscale processing
    if (durationPredicted > 0) {
      setProgressAnchor(jobId, {
        anchorUptime: performance.now(),
        anchorProgress: 0,
        durationPredicted,
      });
    }

    set((state) => ({
      jobQueue: state.jobQueue.map((job) =>
        job.id === jobId
          ? {
              ...job,
              status: RenderStatus.PROCESSING,
              startedAt,
              durationPredicted,
            }
          : job
      ),
    }));
  },

  transitionJob: (sourceId, newId, startedAt, durationPredicted) => {
    // Remove old progress anchor
    removeProgressAnchor(sourceId);

    // Update protection cache: remove old ID, add new one
    const cachedEntry = lastProcessingJobs.get(sourceId);
    if (cachedEntry) {
      lastProcessingJobs.delete(sourceId);
    }

    set((state) => {
      const sourceIndex = state.jobQueue.findIndex((j) => j.id === sourceId);
      if (sourceIndex === -1) return state;

      const sourceJob = state.jobQueue[sourceIndex];
      const newJob: Job = {
        ...sourceJob,
        id: newId,
        status: RenderStatus.PROCESSING,
        startedAt,
        durationPredicted,
      } as Job;

      // Add new job to protection cache
      lastProcessingJobs.set(newId, { job: newJob, timestamp: Date.now() });

      const newQueue = [...state.jobQueue];
      newQueue[sourceIndex] = newJob;
      return { jobQueue: newQueue };
    });
  },

  removeUpscaleFromProtectionCache: (originalRenderId) => {
    // Find upscale job in protection cache by originalRenderId and remove it
    let foundJobId: string | null = null;

    lastProcessingJobs.forEach((cached, jobId) => {
      if (
        cached.job.jobType === 'upscale' &&
        (cached.job as UpscaleJob).originalRenderId === originalRenderId
      ) {
        foundJobId = jobId;
      }
    });

    if (foundJobId) {
      lastProcessingJobs.delete(foundJobId);
      removeProgressAnchor(foundJobId);
      recordTerminalState(foundJobId);
    }
  },

  removeUpscaleJobById: (jobId) => {
    // Remove upscale job directly by its ID (upscale_render_id)
    if (lastProcessingJobs.has(jobId)) {
      lastProcessingJobs.delete(jobId);
      removeProgressAnchor(jobId);
      recordTerminalState(jobId);
    }
  },

  // ===== L4 WATCHDOG (WEB-L4) =====
  // Drop a job that the server no longer recognizes (`GET /renders/{id}` →
  // 404). See docs/REALTIME_RECONCILE_PLAN.md → WEB-L4.
  removeJobById: (jobId) => {
    const state = get();
    if (!state.jobQueue.some((j) => j.id === jobId)) {
      // Already gone — still wipe internals defensively (a previous tick
      // could have left bookkeeping behind).
      lastProcessingJobs.delete(jobId);
      removeProgressAnchor(jobId);
      return;
    }
    lastProcessingJobs.delete(jobId);
    removeProgressAnchor(jobId);
    recordTerminalState(jobId);
    set({
      jobQueue: state.jobQueue.filter((j) => j.id !== jobId),
    });
  },
});
